import { useState } from 'react';
import { Plus, Disc, Upload, Edit, Trash2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../context/AuthContext';
import { API, toFullUrl } from '../../config/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { BulkAlbumDialog } from './BulkAlbumDialog';

const EMPTY_FORM = {
  title: '', artist: '', description: '', cover_image: '', release_date: '',
  album_type: 'album', upc_code: '', label: '', catalog_number: '',
  is_purchasable: false, price: 0, on_sale: false, sale_price: null, download_file: '',
};

export function AlbumsPanel({ albums, setAlbums, onRefresh }) {
  const [bulkOpen, setBulkOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const resetForm = () => { setForm(EMPTY_FORM); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`${API}/albums/${editing.id}`, form, { withCredentials: true });
        toast.success('Album updated!');
      } else {
        await api.post(`${API}/albums`, form, { withCredentials: true });
        toast.success('Album created!');
      }
      setDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save album');
    }
  };

  const handleDelete = async (albumId) => {
    if (!window.confirm('Delete this album? Tracks will be kept but unassigned.')) return;
    try {
      await api.delete(`${API}/albums/${albumId}`, { withCredentials: true });
      toast.success('Album deleted!');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete album');
    }
  };

  const toggleHomeFeatured = async (album) => {
    try {
      const { data } = await api.put(`${API}/albums/home-featured/${album.id}`);
      setAlbums(albums.map(a => a.id === album.id
        ? { ...a, home_featured: data.featured, home_featured_order: data.featured ? data.count - 1 : undefined }
        : a));
      toast.success(data.featured ? `★ Featured on homepage (${data.count}/6)` : '☆ Removed from homepage');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toggle failed');
    }
  };

  const openEdit = (album) => {
    setEditing(album);
    setForm({ ...EMPTY_FORM, ...album });
    setDialogOpen(true);
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error('Cover must be under 20MB'); return; }
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd);
      setForm(prev => ({ ...prev, cover_image: data.url }));
      toast.success('Cover uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="albums-content">
      <div className="flex items-center justify-between">
        <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter">ALBUMS & PLAYLISTS</h2>
        <div className="flex items-center gap-2">
          <Button onClick={() => setBulkOpen(true)}
            className="bg-transparent border border-[#262626] hover:border-[#FF3B30] hover:text-[#FF3B30] text-xs font-bold uppercase rounded-none"
            data-testid="bulk-album-btn">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> BULK
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="add-album-btn">
                <Plus className="w-4 h-4 mr-2" /> ADD ALBUM
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-md">
              <DialogHeader>
                <DialogTitle className="font-cabinet text-xl font-black uppercase">
                  {editing ? 'EDIT ALBUM' : 'ADD NEW ALBUM'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label className="overline">Title</Label>
                  <Input value={form.title}
                    onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" required data-testid="album-title-input" />
                </div>
                <div>
                  <Label className="overline">Artist</Label>
                  <Input value={form.artist}
                    onChange={(e) => setForm(p => ({ ...p, artist: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" required data-testid="album-artist-input" />
                </div>
                <div>
                  <Label className="overline">Type</Label>
                  <select value={form.album_type}
                    onChange={(e) => setForm(p => ({ ...p, album_type: e.target.value }))}
                    className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] p-2 text-white"
                    data-testid="album-type-select">
                    <option value="album">Album</option>
                    <option value="ep">EP</option>
                    <option value="single">Single</option>
                    <option value="playlist">Playlist</option>
                  </select>
                </div>
                <div>
                  <Label className="overline">Cover Image</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="w-20 h-20 bg-[#0A0A0A] border border-[#262626] overflow-hidden flex items-center justify-center shrink-0">
                      {form.cover_image ? (
                        <img src={toFullUrl(form.cover_image)} alt="" className="w-full h-full object-cover" />
                      ) : <ImageIcon className="w-7 h-7 text-[#262626]" />}
                    </div>
                    <div className="flex-1">
                      <input type="file" accept="image/*" className="hidden" id="album-cover-upload"
                        onChange={(e) => handleCoverUpload(e.target.files?.[0])} />
                      <label htmlFor="album-cover-upload"
                        className="cursor-pointer block text-center px-3 py-2 border border-[#262626] hover:border-[#FF3B30] hover:text-[#FF3B30] text-xs font-bold uppercase transition-colors"
                        data-testid="album-cover-upload-btn">
                        {form.cover_image ? 'CHANGE COVER' : 'UPLOAD COVER'}
                      </label>
                      <Input value={form.cover_image}
                        onChange={(e) => setForm(p => ({ ...p, cover_image: e.target.value }))}
                        className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none text-[10px]"
                        placeholder="or paste a URL" data-testid="album-cover-input" />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="overline">Release Date</Label>
                  <Input type="date" value={form.release_date}
                    onChange={(e) => setForm(p => ({ ...p, release_date: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="album-release-date-input" />
                </div>

                {/* Distribution Info */}
                <div className="border-t border-[#262626] pt-4">
                  <Label className="overline mb-3 block text-[#FF3B30]">Distribution Info</Label>
                  <div>
                    <Label className="text-xs text-[#A3A3A3]">UPC/EAN Code</Label>
                    <Input value={form.upc_code}
                      onChange={(e) => setForm(p => ({ ...p, upc_code: e.target.value }))}
                      className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                      placeholder="012345678901" data-testid="album-upc-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label className="text-xs text-[#A3A3A3]">Record Label</Label>
                      <Input value={form.label}
                        onChange={(e) => setForm(p => ({ ...p, label: e.target.value }))}
                        className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                        placeholder="Your label name" data-testid="album-label-input" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#A3A3A3]">Catalog Number</Label>
                      <Input value={form.catalog_number}
                        onChange={(e) => setForm(p => ({ ...p, catalog_number: e.target.value }))}
                        className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                        placeholder="CAT-001" data-testid="album-catalog-input" />
                    </div>
                  </div>
                </div>

                {/* Album as Product */}
                <div className="border-t border-[#262626] pt-4">
                  <Label className="overline mb-3 block text-[#DAA520]">Sell as Bundle</Label>
                  <div className="flex items-center justify-between bg-[#0A0A0A] border border-[#262626] p-3 rounded-md">
                    <div>
                      <p className="text-xs font-bold uppercase">Purchasable Album</p>
                      <p className="text-[10px] text-[#737373]">Customers can buy the whole album, even if individual tracks have no streaming link</p>
                    </div>
                    <Switch checked={!!form.is_purchasable}
                      onCheckedChange={(v) => setForm(p => ({ ...p, is_purchasable: v }))}
                      data-testid="album-purchasable-toggle" />
                  </div>
                  {form.is_purchasable && (
                    <div className="space-y-3 mt-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-[#A3A3A3]">Price (USD)</Label>
                          <Input type="number" step="0.01" min="0" value={form.price ?? 0}
                            onChange={(e) => setForm(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))}
                            className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                            data-testid="album-price-input" />
                        </div>
                        <div>
                          <Label className="text-xs text-[#A3A3A3]">Sale Price (optional)</Label>
                          <Input type="number" step="0.01" min="0" value={form.sale_price ?? ''}
                            onChange={(e) => setForm(p => ({ ...p, sale_price: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                            className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                            placeholder="empty = no sale" data-testid="album-sale-price-input" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={!!form.on_sale}
                          onCheckedChange={(v) => setForm(p => ({ ...p, on_sale: v }))}
                          data-testid="album-on-sale-toggle" />
                        <Label className="text-xs">On Sale</Label>
                      </div>
                      <div>
                        <Label className="text-xs text-[#A3A3A3]">Album ZIP / Archive URL (optional)</Label>
                        <Input value={form.download_file || ''}
                          onChange={(e) => setForm(p => ({ ...p, download_file: e.target.value }))}
                          className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                          placeholder="Leave empty to auto-build from tracks"
                          data-testid="album-download-input" />
                        <p className="text-[10px] text-[#525252] mt-1">
                          If empty, a ZIP is built on-the-fly from all tracks in this album that have a downloadable file.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="album-submit-btn">
                  {editing ? 'UPDATE ALBUM' : 'CREATE ALBUM'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {albums.length === 0 ? (
        <div className="bg-[#141414] border border-[#262626] p-12 text-center">
          <Disc className="w-12 h-12 mx-auto text-[#A3A3A3] mb-4" />
          <p className="text-[#A3A3A3]">No albums yet. Create your first album!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {albums.map(album => (
            <div key={album.id} className="bg-[#141414] border border-[#262626] p-4" data-testid={`album-card-${album.id}`}>
              <div className="flex gap-4">
                {album.cover_image ? (
                  <img src={toFullUrl(album.cover_image)} alt={album.title} className="w-20 h-20 object-cover" />
                ) : (
                  <div className="w-20 h-20 bg-[#262626] flex items-center justify-center">
                    <Disc className="w-8 h-8 text-[#A3A3A3]" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-bold">{album.title}</h3>
                  <p className="text-[#A3A3A3] text-sm">{album.artist}</p>
                  <p className="text-[#FF3B30] text-xs mt-1 uppercase">{album.album_type}</p>
                  <p className="text-[#A3A3A3] text-xs mt-1">{album.track_count || 0} tracks</p>
                  {album.is_purchasable && album.price > 0 && (
                    <p className="text-[#22C55E] text-xs mt-1 font-mono font-bold" data-testid={`album-bundle-badge-${album.id}`}>
                      BUNDLE ${(album.on_sale && album.sale_price != null ? album.sale_price : album.price).toFixed(2)}
                    </p>
                  )}
                </div>
                <button onClick={() => toggleHomeFeatured(album)}
                  title={album.home_featured ? `Featured on homepage (#${(album.home_featured_order ?? 0) + 1}/6) — click to remove` : 'Show on homepage (max 6)'}
                  className={`self-start text-lg leading-none transition-colors ${album.home_featured ? 'text-[#DAA520] hover:text-[#FFD700]' : 'text-[#404040] hover:text-[#DAA520]'}`}
                  data-testid={`album-home-feature-${album.id}`}>
                  {album.home_featured ? '★' : '☆'}
                </button>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => openEdit(album)}
                  className="flex-1 rounded-none border-[#262626]" data-testid={`edit-album-${album.id}`}>
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(album.id)}
                  className="rounded-none border-[#262626] text-[#FF3B30]" data-testid={`delete-album-${album.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {bulkOpen && (
        <BulkAlbumDialog onClose={() => setBulkOpen(false)} onSaved={() => { onRefresh(); setBulkOpen(false); }} />
      )}
    </div>
  );
}
